import React, { useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import BattleCanvas from './components/BattleCanvas';
import StatsBoard from './components/StatsBoard';
import GameControls from './components/GameControls';
import SeoHead from './components/SeoHead';
import SiteFooter from './components/SiteFooter';
import { useGameLoop } from './hooks/useGameLoop';
import './App.css';

const PUBLISHED_DATE = '2026-03-07';
const UPDATED_DATE = '2026-03-07';
const DISPLAY_DATE = '2026 年 3 月 7 日';

function ContentDates() {
  return (
    <p
      className="mt-1 text-[9px] font-bold tracking-wide text-white/50 md:text-[10px]"
      aria-label={`發布日期 ${DISPLAY_DATE}，更新日期 ${DISPLAY_DATE}`}
    >
      發布於
      {' '}
      <time dateTime={PUBLISHED_DATE} itemProp="datePublished">
        {DISPLAY_DATE}
      </time>
      <span className="mx-1.5 text-white/25" aria-hidden="true">
        ·
      </span>
      更新於
      {' '}
      <time dateTime={UPDATED_DATE} itemProp="dateModified">
        {DISPLAY_DATE}
      </time>
    </p>
  );
}

function App() {
  const canvasRef = useRef(null);
  const {
    agents,
    stats,
    totalRounds,
    winCounts,
    isRunning,
    setIsRunning,
    winner,
    fullReset
  } = useGameLoop(canvasRef);

  return (
    <HelmetProvider>
      <SeoHead />

      <article
        className="game-wrapper flex flex-col items-center overflow-hidden font-inter text-white"
        itemScope
        itemType="https://schema.org/SoftwareApplication"
      >
        <div className="stars-container" />

        <div className="relative z-10 flex h-[100dvh] w-full max-w-5xl flex-col p-3 md:p-6 lg:p-8">
          <header className="mb-4 flex shrink-0 items-end justify-between px-1">
            <div className="flex min-w-0 flex-col">
              <h1
                className="truncate text-xl font-black leading-tight md:text-3xl lg:text-4xl"
                itemProp="name"
              >
                <span className="aurora-title">
                  ✊🏼 ✂️ 🖐 大亂鬥
                </span>
              </h1>

              <p
                className="mt-1 text-[8px] font-black uppercase tracking-[0.15em] text-cyan-400/60 md:text-[10px]"
                itemProp="description"
              >
                RPS Chaos: Advanced Physics Simulation 2026
              </p>

              <ContentDates />
            </div>

            <div className="ml-4 flex shrink-0 flex-col items-end">
              <div className="mb-1 text-[10px] font-bold uppercase leading-none tracking-widest text-white/50">
                Round
              </div>
              <div className="font-mono text-xl font-black leading-none text-white/90 md:text-3xl">
                #{totalRounds.toString().padStart(3, '0')}
              </div>
            </div>
          </header>

          <main className="relative flex min-h-0 flex-1 flex-col">
            <div className="relative min-h-0 flex-1 overflow-hidden rounded-[24px] border border-white/10 bg-black/40 shadow-2xl md:rounded-[32px]">
              <BattleCanvas
                ref={canvasRef}
                agents={agents}
                winner={winner}
              />
            </div>

            <div className="mt-4 shrink-0">
              <StatsBoard
                stats={stats}
                winCounts={winCounts}
                totalRounds={totalRounds}
              />

              <div className="mt-3 flex items-center justify-between gap-2 md:gap-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="relative flex shrink-0">
                    <div className="absolute inset-0 h-2 w-2 animate-ping rounded-full bg-cyan-500" />
                    <div className="relative z-10 h-2 w-2 rounded-full bg-cyan-400" />
                  </div>

                  <div className="flex min-w-0 flex-col">
                    <span className="text-[10px] font-black leading-none tracking-wider text-white/95 md:text-[11px]">
                      系統狀態: 引擎運作中
                    </span>
                    <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.1em] text-white/50">
                      Deterministic Chaos Engine v2.6
                    </span>
                  </div>
                </div>

                <div className="flex flex-1 justify-center">
                  <GameControls
                    isRunning={isRunning}
                    onToggle={() => setIsRunning(!isRunning)}
                    onReset={fullReset}
                  />
                </div>

                <div className="hidden text-right sm:block">
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/40">
                    Homy Tech Studio
                  </p>
                </div>
              </div>
            </div>
          </main>

          <SiteFooter />
        </div>
      </article>
    </HelmetProvider>
  );
}

export default App;